import { useContext, useState } from 'react';
import * as R from 'ramda';

import DeleteModal from '@atom/components/common/DeleteModal';
import MaterialModal from '@atom/components/common/workOrderDetail/materialModal/MaterialModal';
import WorkOrderContext from '@atom/components/workOrderDetail/WorkOrderContext';
import { Icon, IconButton, Snackbar, Tooltip } from '@atom/mui';
import { CategoryIcon, CloseIcon } from '@atom/mui/icons';
import { TaskMaterialItem } from '@atom/types/task';
import api from '@atom/utilities/api';
import {
  doesNotHaveRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import { numberToLocaleString } from '@atom/utilities/currencyUtility';
import { WORK_ORDERS_ENDPOINT } from '@atom/utilities/endpoints';
import schemaUtilities from '@atom/utilities/schemaUtilities';

import classes from './materialSection.module.css';

interface Props {
  material: TaskMaterialItem;
}

const MaterialTile = ({ material }: Props) => {
  const { workOrderDetail, task, refetch } = useContext(WorkOrderContext);

  const [materialModalOpen, setMaterialModalOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const iconStyle = {
    color: schemaUtilities.getSchemaColorFromColorId(material.colorId),
  };

  const costSubtext = `${material.quantity.toFixed(2) || 0} ${
    material.unit
  } - ${numberToLocaleString(material.cost.toFixed(2))}`;

  const isEditDisabled =
    workOrderDetail.isClosed || doesNotHaveRolePermissions(ROLE_SETS.INSPECTOR);

  const removeMaterial = async () => {
    setDeleteModalOpen(false);

    Snackbar.info({
      message: 'Deleting...',
    });

    const endpoint = `${WORK_ORDERS_ENDPOINT}/${workOrderDetail.id}/tasks/${task.id}/materials/${material.assetId}`;

    await api.delete(endpoint);

    Snackbar.info({
      message: `Removed Material.`,
    });

    refetch();
  };

  return (
    <>
      <div className={classes.tileContainer}>
        <div
          className={classes.tileContentContainer}
          onClick={() => setMaterialModalOpen(true)}
          data-cy={`materialTile-${material.name}`}
        >
          <div className={classes.tileIconContainer}>
            {!R.isNil(material.locationType) ? (
              schemaUtilities.getSchemaIconFromSchemaOrAsset(material, {
                imgOffset: '0',
                iconSize: '24px',
                marginTop: '0',
              })
            ) : (
              <Icon style={iconStyle} icon={CategoryIcon} />
            )}
          </div>
          <div className={classes.tileContent}>
            <Tooltip title={material.name}>
              <div className={classes.tileTitle}>{material.name}</div>
            </Tooltip>
            <div className={classes.tileSubtext}>{costSubtext}</div>
          </div>
        </div>
        {!isEditDisabled && (
          <IconButton
            onClick={() => setDeleteModalOpen(true)}
            tooltip="Remove"
            edge="end"
            data-cy={`materialRemoveButton-${material.name}`}
          >
            <Icon icon={CloseIcon} />
          </IconButton>
        )}
      </div>
      <MaterialModal
        onClose={() => setMaterialModalOpen(false)}
        open={materialModalOpen}
        material={material}
        task={task}
        workOrderDetail={workOrderDetail}
        refetch={refetch}
      />
      <DeleteModal
        open={deleteModalOpen}
        onConfirm={removeMaterial}
        onCancel={() => setDeleteModalOpen(false)}
        title="Remove Equipment or Material From Task"
        content="Are you sure you want to remove this item from the task?"
        cancelText="Cancel"
        confirmText="Remove"
      />
    </>
  );
};

export default MaterialTile;
